import { useCallback, useEffect, useMemo, useState } from "react";
import { useClient } from "urql";

const externalReviewerGroupsQuery = `
  query ExternalReviewerGroups($program: ID!) {
    externalReviewerGroups(program: $program) {
      id
      name
      reviewers
    }
  }`;

// Loads the External Reviewer Groups for the selected content's program(s) and
// exposes the helpers the dialog needs to turn group selections into concrete
// reviewer assignments. Kept out of the UI component so the data/business layer
// is independently testable.
//
// - `programs`: the program IDs the selected content spans (groups are unioned
//   across them, mirroring how the reviewer pool unions External Reviewers).
// - `users`: the available reviewer pool (group members are restricted to it).
// - `enabled`: only fetch when the Groups picker applies (External Review).
//
// Returns `{ reviewerGroups, groupOptions, resolveAssignedUsers }`.
const useExternalReviewerGroups = ({ programs, users, enabled }) => {
  const client = useClient();
  const [reviewerGroups, setReviewerGroups] = useState([]);

  useEffect(() => {
    if (!enabled || !programs.length) return undefined;
    let active = true;
    Promise.all(
      programs.map((program) =>
        client.query(externalReviewerGroupsQuery, { program }).toPromise(),
      ),
    )
      .then((results) => {
        if (!active) return;
        // Programs can share a group, so dedupe by id to avoid duplicate
        // options (and duplicate React keys) in the Groups picker.
        const byId = new Map(
          results
            .flatMap((result) => result.data?.externalReviewerGroups || [])
            .map((group) => [group.id, group]),
        );
        setReviewerGroups([...byId.values()]);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Failed to load External Reviewer Groups:", error);
        setReviewerGroups([]);
      });
    return () => {
      active = false;
    };
  }, [client, programs, enabled]);

  const groupOptions = useMemo(
    () =>
      reviewerGroups.map((group) => ({ label: group.name, value: group.id })),
    [reviewerGroups],
  );

  // The reviewers a section ultimately assigns: either the manually added
  // reviewers, or — when groups are chosen instead — the deduped union of the
  // selected groups' members, restricted to the available reviewer pool so no
  // inactive / out-of-program reviewer is silently assigned (AC 6).
  const validUserIds = useMemo(
    () => new Set(users.map((user) => user.value)),
    [users],
  );
  const resolveAssignedUsers = useCallback(
    (section) => {
      if (section.reviewerGroupIds?.length) {
        const reviewerIds = new Set();
        section.reviewerGroupIds.forEach((groupId) => {
          const group = reviewerGroups.find((rg) => rg.id === groupId);
          (group?.reviewers || []).forEach((id) => {
            if (validUserIds.has(id)) reviewerIds.add(id);
          });
        });
        return [...reviewerIds];
      }
      return [...section.assignedUsers];
    },
    [reviewerGroups, validUserIds],
  );

  return { groupOptions, resolveAssignedUsers };
};

export { externalReviewerGroupsQuery, useExternalReviewerGroups };
